import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FiPhoneCall, FiArrowRight, FiShare2 } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import OmSymbol from "./OmSymbol";

export default function PersonCard({ person, role, ctaLink, ctaLabel, reverse = false, badge }) {
  const [imgError, setImgError] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const cardRef = useRef(null);

  const waLink = buildWhatsAppLink(person.whatsapp, `Namaste 🙏, I would like to enquire about your services.`);

  const handleShare = async () => {
    if (!cardRef.current) return;

    try {
      setIsSharing(true);

      // We temporarily clone the node to render a clean version without buttons for the image
      const clone = cardRef.current.cloneNode(true);
      // Remove the action buttons row from the clone
      const buttonsRow = clone.querySelector('.actions-row');
      if (buttonsRow) buttonsRow.remove();

      // Add a watermark or website URL to the bottom of the clone
      const footer = document.createElement('div');
      footer.innerHTML = '<p style="text-align: center; margin-top: 15px; color: #7f1d1d; font-weight: bold; font-size: 14px;">Visit us at: www.tathastujotishalaya.com</p>';
      clone.querySelector('.content-container').appendChild(footer);

      // Temporarily append clone to body to render it
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.width = '800px'; // fixed width for a nice card
      clone.style.background = 'white';
      clone.style.padding = '40px';
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 2, // High quality
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(clone);

      // Convert to blob
      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const file = new File([blob], `${person.name.replace(/\s+/g, '_')}_Card.png`, { type: 'image/png' });

        // Try using the Web Share API if available (works on mobile)
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Contact ${person.name}`,
            text: `Connect with ${person.name} - ${role} at Tathastu Jotishalaya.`,
          });
        } else {
          // Fallback: Download the image
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${person.name.replace(/\s+/g, '_')}_Card.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          alert("Card downloaded! You can now share it manually.");
        }
      }, 'image/png');

    } catch (error) {
      console.error("Error generating share image:", error);
      alert("Could not generate share image.");
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-12 bg-white p-8 lg:p-10 rounded-3xl shadow-premium border border-gold-100 hover-lift relative`}
    >
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleShare}
          disabled={isSharing}
          className="p-2.5 bg-gray-50 text-gray-500 hover:text-maroon-700 hover:bg-gold-50 rounded-full transition-colors flex items-center justify-center border border-gray-200"
          title="Share as Image"
        >
          <FiShare2 size={18} />
        </button>
      </div>

      <div className="w-full lg:w-1/4 flex-shrink-0 relative z-10 mx-auto">
        <div className="relative">
          {/* soft glow ring behind portrait */}
          <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-gold-300/40 to-saffron-200/30 blur-lg" />
          <div className="relative aspect-[3/4] max-w-[240px] mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-saffron-50 to-gold-50 flex items-center justify-center border-4 border-white shadow-premium-lg ring-1 ring-gold-300/40">
            {!imgError ? (
              <img
                src={person.image}
                alt={person.name}
                onError={() => setImgError(true)}
                className="w-full h-full object-cover object-top"
                crossOrigin="anonymous" // needed for html2canvas to render images
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-maroon-700 p-8">
                <span className="text-6xl mb-3 flex justify-center"><OmSymbol size={56} /></span>
                <p className="font-heading text-xl text-center">{person.name}</p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          {badge && (
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-maroon-gradient text-gold-200 px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-semibold tracking-wider whitespace-nowrap shadow-gold z-20 ring-1 ring-gold-300/30">
              {badge}
            </div>
          )}
        </div>
      </div>

      <div className="w-full lg:w-3/4 relative z-10 content-container">
        <p className="text-saffron-600 font-semibold uppercase tracking-widest text-sm mb-2 text-center lg:text-left">{role}</p>
        <h3 className="font-heading text-3xl lg:text-4xl text-maroon-800 mb-6 text-center lg:text-left">{person.name}</h3>

        <div className="text-sm text-gray-600 leading-relaxed mb-8 text-center lg:text-left">
            <p>{person.intro}</p>
        </div>

        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 actions-row">
          <a
            href={`tel:+91${person.phone}`}
            className="inline-flex items-center gap-2 bg-maroon-50 text-maroon-800 hover:bg-maroon-800 hover:text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
          >
            <FiPhoneCall size={16} /> {person.phoneDisplay}
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-50 text-green-700 hover:bg-green-500 hover:text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
          >
            <FaWhatsapp size={18} /> WhatsApp
          </a>
          {ctaLink && (
            <Link
              to={ctaLink}
              className="inline-flex items-center gap-1 text-saffron-600 font-semibold text-sm hover:gap-2 transition-all ml-2"
            >
              {ctaLabel} <FiArrowRight />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}
